import React from 'react'

import Image from 'next/image'

import { Button, Row, Text } from 'Components/UI'

import {
  MobileScreensCol1Image,
  MobileScreensCol2Image,
  MobileScreensCol3Image,
} from 'Assets/Image'
import {
  LogoAfwerxImage,
  LogoEYImage,
  LogoPhilipsImage,
  LogoPWCImage,
  LogoUHGImage,
} from 'Assets/Image/CompanyLogos'

import { useAppContext } from 'Services/AppContext'

import {
  ClientLogos,
  Container,
  Content,
  HeroCopy,
  Visual,
  VisualColumn,
  VisualColumns,
  VisualFrame,
} from './styles'

const CLIENT_LOGOS = [
  { alt: 'Philips', src: LogoPhilipsImage },
  { alt: 'AFWERX', src: LogoAfwerxImage },
  { alt: 'PwC', src: LogoPWCImage },
  { alt: 'EY', src: LogoEYImage },
  { alt: 'UnitedHealth Group', src: LogoUHGImage },
] as const

// Measured slot widths (notes/scripts/measure-slots.mjs, cache disabled):
// 1290px+ -> 210 CSS px, 768-1024px -> 266-294, 390-412px -> 31vw. Without a
// `sizes` the browser assumes 100vw and takes the 640w candidate for a 210px
// slot — 1.4 MB wasted on the homepage.
const COLLAGE_SIZES = '(min-width: 992px) 210px, (min-width: 768px) 300px, 32vw'

function HomeHeroSection() {
  const { openSupportWidget } = useAppContext()

  return (
    <Container>
      <Content>
        <HeroCopy>
          <Text
            as="h1"
            medium
            mt={['40px', '40px', '40px', 'auto']}
            title
            title52
          >
            Ship real products
            <br />
            <span style={{ fontWeight: 300 }}>at warp speed</span>
          </Text>

          <Text as="h2" body body16 mt={7} semiLight>
            Custom software and AI development, Sydney. Built by engineers — not
            vibe coders.
          </Text>

          <Row mb={['40px', '40px', '40px', 'auto', 'auto', 'auto']} mt="40px">
            <Button height="48px" type="button" onClick={openSupportWidget}>
              <Text as="span" body16 medium>
                Book a paid discovery
              </Text>
            </Button>
          </Row>

          <Text body12 mb="40px" mt={7} muted semiLight uppercase>
            Trusted by World-class brands
          </Text>

          <ClientLogos>
            {CLIENT_LOGOS.map(({ alt, src }) => (
              <Image alt={alt} key={alt} src={src} />
            ))}
          </ClientLogos>
        </HeroCopy>

        <Visual>
          <VisualFrame>
            <VisualColumns>
              <VisualColumn direction="down">
                {/* Exactly one collage strip is eager, and it has to be this
                    one: Lighthouse names this image (620x4006, the first
                    column) as the desktop LCP element, so it is the only strip
                    worth the single `<link rel=preload as=image>` that
                    `priority` emits. It previously sat on column 2, which
                    spent the preload on an image that is never the LCP while
                    the real one stayed `loading="lazy"` — `lcp-discovery`
                    failed both its "fetchpriority=high" and "should not use
                    loading=lazy" checks in production because of it.
                    Do not add `priority` to the other strips: on mobile the
                    LCP is the h1, and competing image preloads measurably cost
                    the text paint (Phase 4.5 lost 12 mobile points that way). */}
                <Image
                  alt=""
                  fetchPriority="high"
                  priority
                  sizes={COLLAGE_SIZES}
                  src={MobileScreensCol1Image}
                />
                <Image
                  alt=""
                  sizes={COLLAGE_SIZES}
                  src={MobileScreensCol1Image}
                />
              </VisualColumn>
              <VisualColumn direction="up">
                <Image
                  alt=""
                  sizes={COLLAGE_SIZES}
                  src={MobileScreensCol2Image}
                />
                <Image
                  alt=""
                  sizes={COLLAGE_SIZES}
                  src={MobileScreensCol2Image}
                />
              </VisualColumn>
              <VisualColumn direction="down">
                <Image
                  alt=""
                  sizes={COLLAGE_SIZES}
                  src={MobileScreensCol3Image}
                />
                <Image
                  alt=""
                  sizes={COLLAGE_SIZES}
                  src={MobileScreensCol3Image}
                />
              </VisualColumn>
            </VisualColumns>
          </VisualFrame>
        </Visual>
      </Content>
    </Container>
  )
}

export default HomeHeroSection
